import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  EnvelopeSimple, PaperPlaneTilt, CheckCircle,
  ShieldCheck, LockKey, Envelope, ClockCounterClockwise,
  SignIn, ShieldStar,
} from "@phosphor-icons/react";

export default function Login() {
  const { requestLink, loginPassword, verifyTwoFa } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("magic"); // "magic" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentMessage, setSentMessage] = useState("");
  const [error, setError] = useState("");
  // 2FA challenge state (same as MagicLinkVerify)
  const [twoFa, setTwoFa] = useState(null);   // { challenge_id, email }
  const [tfaCode, setTfaCode] = useState("");
  const [tfaSubmitting, setTfaSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "magic") {
      const r = await requestLink(email.trim());
      setLoading(false);
      if (r.ok) setSentMessage(r.message);
      else setError(r.error);
    } else {
      const r = await loginPassword(email.trim(), password);
      setLoading(false);
      if (r.ok) {
        navigate("/app", { replace: true });
      } else if (r.twoFa) {
        setTwoFa(r.twoFa);
      } else {
        setError(r.error);
      }
    }
  };

  const submitTfa = async (e) => {
    e?.preventDefault?.();
    if (!twoFa || !tfaCode.trim()) return;
    setTfaSubmitting(true);
    setError("");
    const r = await verifyTwoFa(twoFa.challenge_id, tfaCode.trim());
    setTfaSubmitting(false);
    if (r.ok) navigate("/app", { replace: true });
    else setError(r.error);
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setSentMessage("");
    setPassword("");
    setTwoFa(null);
    setTfaCode("");
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-brand-navy overflow-hidden relative">
      {/* subtle radial gradient blob for depth */}
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,199,255,0.10) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,199,255,0.06) 0%, transparent 60%)" }} />
      </div>

      {/* LEFT: hero panel */}
      <div className="relative flex items-center px-8 sm:px-14 lg:px-20 py-16 lg:py-0 z-10">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-brand-cyan/40 bg-brand-cyan/5 mb-10"
            data-testid="login-hero-badge">
            <ShieldCheck size={13} weight="fill" className="text-brand-cyan" />
            <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-brand-cyan">Secure client onboarding</span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[0.95]">
            Enterprise IT.<br />
            Speed. Strength.<br />
            <span className="text-brand-cyan">Security.</span>
          </h1>

          <p className="mt-8 text-base sm:text-lg text-slate-300 leading-relaxed max-w-md">
            Your secure portal to onboard with Sentry Cloud IT.
            Passwordless sign-in, encrypted in transit, audited end-to-end.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-slate-300">
            <FeatureChip icon={LockKey}                label="Single-use links" />
            <FeatureChip icon={Envelope}               label="No passwords" />
            <FeatureChip icon={ClockCounterClockwise}  label="Full audit log" />
          </div>
        </div>
      </div>

      {/* RIGHT: sign-in card */}
      <div className="relative flex items-center justify-center px-6 sm:px-10 py-10 lg:py-16 z-10">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="login-card"
          style={{ boxShadow: "0 25px 60px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" }}>
          {/* navy header with logo */}
          <div className="bg-brand-navy px-8 py-8 flex items-center justify-center">
            <Logo size={100} />
          </div>

          {/* body */}
          <div className="px-8 pt-8 pb-6">
            {sentMessage ? (
              <div className="text-center py-4" data-testid="login-sent">
                <CheckCircle size={44} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy mb-2">Check your inbox</h1>
                <p className="text-sm text-slate-600 leading-relaxed">{sentMessage}</p>
                <p className="text-xs text-slate-400 mt-6">
                  The link expires in 15 minutes. You can close this tab.
                </p>
                <button onClick={() => { setSentMessage(""); setEmail(""); }}
                  data-testid="login-send-another"
                  className="mt-5 text-sm text-brand-navy hover:text-brand-cyan font-semibold underline underline-offset-4">
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                <h1 className="font-display text-3xl font-bold tracking-tight text-brand-navy text-center">Sign in</h1>
                <p className="text-sm text-slate-500 text-center mt-2 mb-8">
                  {mode === "magic"
                    ? "We\u2019ll email you a one-click sign-in link."
                    : "Password sign-in — admin accounts only."}
                </p>

                <form onSubmit={twoFa ? submitTfa : onSubmit} className="space-y-5" data-testid="login-form">
                  {/* Mode switcher — magic link vs password */}
                  {!twoFa && (
                    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg border border-slate-200 mb-1"
                      data-testid="login-mode-switcher">
                      <button type="button"
                        onClick={() => switchMode("magic")}
                        data-testid="login-mode-magic"
                        className={`flex-1 text-xs font-semibold uppercase tracking-[0.12em] py-2 rounded-md transition-colors ${
                          mode === "magic"
                            ? "bg-white text-brand-navy shadow-sm"
                            : "text-slate-500 hover:text-brand-navy"
                        }`}>
                        Magic link
                      </button>
                      <button type="button"
                        onClick={() => switchMode("password")}
                        data-testid="login-mode-password"
                        className={`flex-1 text-xs font-semibold uppercase tracking-[0.12em] py-2 rounded-md transition-colors ${
                          mode === "password"
                            ? "bg-white text-brand-navy shadow-sm"
                            : "text-slate-500 hover:text-brand-navy"
                        }`}>
                        Password
                      </button>
                    </div>
                  )}

                  {twoFa ? (
                    <>
                      <div className="text-center">
                        <ShieldStar size={36} weight="duotone" className="text-brand-navy mx-auto mb-3" />
                        <h2 className="font-display text-lg font-bold text-brand-navy">Two-factor code</h2>
                        <p className="text-sm text-slate-500 mt-1">
                          Signing in as <span className="font-semibold">{twoFa.email}</span>
                        </p>
                      </div>
                      <div>
                        <label htmlFor="login-tfa" className="block text-sm font-semibold text-brand-navy mb-2">
                          Verification code
                        </label>
                        <input
                          id="login-tfa"
                          type="text"
                          inputMode="text"
                          maxLength={15}
                          autoFocus
                          autoComplete="one-time-code"
                          value={tfaCode}
                          onChange={(e) => setTfaCode(e.target.value)}
                          placeholder="123 456"
                          data-testid="login-tfa-input"
                          className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 font-mono text-center text-lg tracking-[0.4em] text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 transition-all"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label htmlFor="login-email" className="block text-sm font-semibold text-brand-navy mb-2">
                          Work email
                        </label>
                        <input
                          id="login-email"
                          type="email"
                          autoComplete="email"
                          autoFocus
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          data-testid="login-email-input"
                          className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-base text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 transition-all"
                        />
                      </div>

                      {mode === "password" && (
                        <div>
                          <label htmlFor="login-password" className="block text-sm font-semibold text-brand-navy mb-2">
                            Password
                          </label>
                          <input
                            id="login-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="•••••••••••••"
                            data-testid="login-password-input"
                            className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-base text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 transition-all"
                          />
                          <p className="mt-1.5 text-[11px] text-slate-500">
                            Admin accounts only. Forgot it? Switch to Magic link, sign in, then reset from Account.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {error && (
                    <div data-testid="login-error"
                      className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                      {error}
                    </div>
                  )}

                  <button type="submit"
                    disabled={loading || tfaSubmitting}
                    data-testid="login-submit-btn"
                    className="w-full bg-brand-navy hover:bg-brand-navy/90 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg px-4 py-3.5 text-white text-base font-semibold transition-colors inline-flex items-center justify-center gap-2.5 shadow-sm">
                    {twoFa ? (
                      <>
                        <ShieldStar size={18} weight="bold" />
                        {tfaSubmitting ? "Verifying…" : "Verify & sign in"}
                      </>
                    ) : mode === "magic" ? (
                      <>
                        <EnvelopeSimple size={18} weight="bold" />
                        {loading ? "Sending sign-in link…" : "Send sign-in link"}
                      </>
                    ) : (
                      <>
                        <SignIn size={18} weight="bold" />
                        {loading ? "Signing in…" : "Sign in"}
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* footer strip */}
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200">
            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 text-center flex items-center justify-center gap-2 flex-wrap">
              <span>Authorized emails only</span>
              <span className="text-slate-300">·</span>
              <span>Single-use</span>
              <span className="text-slate-300">·</span>
              <span>15 min</span>
              <span className="text-slate-300">·</span>
              <span>Encrypted</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureChip({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-sm">
      <Icon size={16} weight="duotone" className="text-brand-cyan shrink-0" />
      <span className="font-medium text-slate-200">{label}</span>
    </div>
  );
}
